import { useCallback, useEffect, useMemo, useState } from 'react'

import { useLocation, useNavigate } from 'react-router-dom'

import useUpdateQueryParams from '@hooks/useUpdateQueryParams'
import { usePageApi } from '@providers/PageApiProvider'
import { SearchPageApiResponse } from '@providers/types/api-responses'
import { emptyParamsRedirectTo } from '@utils/client'
import { removeTrailing } from '@utils/formatters/string.formatter'
import PageRoutes from '@utils/page-routes'

import { SUBFACETS } from '../utils/constants'
import {
  aggregationToFacet,
  getAggregationCategory,
  getParamCategory,
  getSubFacetCategory,
  isSlugFilter,
} from '../utils/helpers'
import { Facet, FacetTerm } from '../utils/types'

const useFilterItem = (facet: Facet, term: FacetTerm) => {
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  const { category } = facet
  const paramCategory = useMemo(() => getParamCategory(facet), [facet])

  const { count: _count, key } = term

  const { addParam, deleteParams, params } = useUpdateQueryParams({
    pathname:
      removeTrailing('/', pathname) === PageRoutes.musicPath()
        ? PageRoutes.musicSearchPath()
        : pathname,
    beforeNavigate:
      removeTrailing('/', pathname) === PageRoutes.musicSearchPath()
        ? emptyParamsRedirectTo(PageRoutes.musicPath())
        : null,
  })

  const { pageData } = usePageApi<SearchPageApiResponse>()
  const [count, setCount] = useState<number>(_count)
  const [selected, setSelected] = useState<boolean>(
    !!params[paramCategory]?.includes(key) ||
      isSlugFilter(facet.category, key, pageData?.slug_filter)
  )
  const [subFacet, setSubFacet] = useState<Facet | null>(getSubFacet(category, key, pageData))

  useEffect(() => {
    if (pageData?.aggregations) {
      const facet = pageData.aggregations[category].find((f) => f.key === key)
      const docCount = facet ? facet.doc_count : 0
      setCount(docCount)
      setSubFacet(getSubFacet(category, key, pageData))
    }
  }, [pageData])

  useEffect(() => {
    setSelected(
      !!params[paramCategory]?.includes(key) ||
        isSlugFilter(facet.category, key, pageData?.slug_filter)
    )
  }, [params, key, paramCategory, pageData?.slug_filter, facet.category])

  const addFilter = useCallback(
    (category, facet, selected) => {
      if (!selected) {
        addParam(category, facet)
        setSelected(true)
      }
    },
    [params]
  )

  const removeFilter = useCallback(
    (category, name) => {
      const subFacetCategory = SUBFACETS[getAggregationCategory(category)]

      if (subFacetCategory && subFacetCategory[name]) {
        deleteParams({ key: category, value: name }, { key: subFacetCategory[name], value: null })
      } else {
        // If filter is from slug, navigate to /music/search
        if (isSlugFilter(category, name, pageData?.slug_filter)) {
          navigate(PageRoutes.musicSearchPath(search))
        }
        // Otherwise, remove the query param
        else {
          deleteParams({ key: category, value: name })
        }
      }
      setSelected(false)
    },
    [params]
  )

  return { addFilter, removeFilter, count, selected, subFacet }
}

export default useFilterItem

function getSubFacet(
  category: string,
  key: string,
  pageData?: SearchPageApiResponse
): Facet | null {
  const subFacetCategory = getSubFacetCategory(category, key)
  return subFacetCategory && pageData?.aggregations[subFacetCategory]
    ? aggregationToFacet(subFacetCategory, pageData?.aggregations[subFacetCategory], true)
    : null
}
